import React, { FC, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { BillingType, fibiBillingData } from "./data";

interface BillingPeriod {
  monthly: number;
  yearly: number;
}

export interface PlanData {
  id: string;
  title: string;
  price: BillingPeriod;
  description: string;
  features: string[];
  isCustom?: boolean;
}

interface Props {
  data: ReturnType<typeof fibiBillingData>[0];
  currentPlan?: BillingType;
  onButtonClick: (billingType: BillingType, paystackPlan: string) => void;
}

const StarterBillingCard: FC<Props> = ({
  data,
  currentPlan,
  onButtonClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const isHighlighted = currentPlan ? data.id.includes(currentPlan) : false;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const isYearly = billingPeriod === "yearly";

  const onClick = () => {
    if (data.id.includes(BillingType.starter) && isYearly) {
      onButtonClick(BillingType.starterYearly, data.yearPaystackPlan as string);
    } else if (data.id.includes(BillingType.growth) && isYearly) {
      onButtonClick(BillingType.growthYearly, data.yearPaystackPlan as string);
    } else if (data.id.includes(BillingType.essential) && isYearly) {
      onButtonClick(
        BillingType.essentialYearly,
        data.yearPaystackPlan as string
      );
    } else if (data.id.includes(BillingType.starter)) {
      onButtonClick(BillingType.starter, data.monthPaystackPlan as string);
    } else if (data.id.includes(BillingType.growth)) {
      onButtonClick(BillingType.growth, data.monthPaystackPlan as string);
    } else if (data.id.includes(BillingType.essential)) {
      onButtonClick(BillingType.essential, data.monthPaystackPlan as string);
    }
  };

  const formatPrice = (price: number) => {
    return `$${price.toLocaleString()}`;
  };

  const currentPrice =
    billingPeriod === "monthly" ? data.dollarMonthPrice : data.dollarYearPrice;

  return (
    <div
      className={`border rounded-xl p-4 w-full transition-all ${
        isHighlighted ? "border-blue-600 shadow-lg" : "border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-bold">{data.title}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            {data.subText}
          </p>
        </div>
        <button
          onClick={toggleExpand}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </button>
      </div>

      <div className="mb-4">
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-3xl font-semibold tracking-tight">
            {formatPrice(currentPrice)}
          </span>
          <span className="text-gray-500 text-base">
            /{billingPeriod === "monthly" ? "month" : "year"}{" "}
            {billingPeriod === "yearly" &&
              `(save ${formatPrice(
                data.dollarSavedYearPrice
              )}, billed annually)`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setBillingPeriod("monthly");
            }}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              billingPeriod === "monthly"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setBillingPeriod("yearly");
            }}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              billingPeriod === "yearly"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <ul className="space-y-2">
            {data.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-blue-600 mt-0.5">•</span>
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={`mt-4 w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
          isHighlighted
            ? "bg-blue-600 text-white hover:bg-blue-700"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
      >
        Subscribe {billingPeriod === "monthly" ? "Monthly" : "Yearly"}
      </button>
    </div>
  );
};

export default StarterBillingCard;
